import React, { useState, useEffect, useRef } from 'react';
import { ActivityIndicator, View, StyleSheet, Platform, Modal, Pressable, TouchableOpacity, Linking, Share } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import * as Clipboard from 'expo-clipboard';
import { Typography } from './ui';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { isRestrictedRegion } from '../utils/restrictedRegion';
import {
  trackShareClicked,
  trackShareCompleted,
  type ShareChannel,
  type ShareTarget,
} from '../services/shareAnalytics';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../styles/accentText';
import { surfaceFill } from '../styles/platformBackgrounds';
export interface ShareModalProps {
  visible: boolean;
  onClose: () => void;
  /** Fully-qualified URL to share. */
  url: string;
  /** Content title used as the share subject/text. */
  title: string;
  /**
   * Localized message body sent alongside the URL (whatsapp body, telegram
   * caption, etc.). When omitted, falls back to `${title} — ${url}`.
   * The caller is responsible for embedding the URL in this string when a
   * custom message is provided.
   */
  message?: string;
  /**
   * Localized subject line (telegram tweet text). When omitted, falls back to
   * `title`.
   */
  subject?: string;
  /**
   * CORE-274 — optional card rendered above the share targets, showing what is
   * about to be sent. Used by the gamification surfaces, which must display a
   * prepared share card; every other caller keeps the plain sheet.
   */
  preview?: React.ReactNode;
  /** 'ru' | 'en' | 'es'. Defaults to 'ru'. */
  language?: string;
  /**
   * CORE-788 — what this sheet is about, so the share can be counted.
   *
   * Required on purpose. Eighteen surfaces render this modal; an optional prop
   * would let any of them ship silently and the report would under-count
   * without anything failing. The modal knows the channel the user picked but
   * not what is being shared or from where, so the caller has to say.
   */
  analytics: ShareTarget;
  /**
   * CORE-1795 — actions inside Agapella (a repost into the feed) above the external
   * targets. Each names what it does; a disabled row stays visible with its hint, a
   * pending one waits. Absent — the sheet is the plain one, as before.
   */
  actions?: ShareModalAction[];
  /**
   * CORE-1795 — the OS share sheet as one more tile («Ещё»). Only when the device has a
   * sheet and the popup opened instead of it because of `actions`.
   */
  onSystemShare?: () => void;
}

export interface ShareModalAction {
  key: string;
  label: string;
  hint?: string | null;
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
  disabled?: boolean;
  pending?: boolean;
  testID?: string;
}

type ShareKind = 'telegram' | 'whatsapp' | 'facebook' | 'email';

/**
 * Unified share sheet — Telegram, WhatsApp, Facebook, Email + copy link.
 *
 * Used by every shareable surface in the app (cards, detail pages, profiles).
 * Falls back to the system share sheet on native when external app intents
 * aren't installed.
 */
export const ShareModal: React.FC<ShareModalProps> = ({
  visible, onClose, url, title, message, subject, preview, language = 'ru',
  analytics, actions, onSystemShare,
}) => {
  const { isDark, colors: tc } = useTheme();
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  // CORE-788 — the sheet opening is the intent, and closing it without picking
  // anything is a real outcome. Tracked from `visible` rather than from the
  // caller's button, because every caller opens it differently and the modal is
  // the one place that sees both edges.
  const target = useRef(analytics);
  target.current = analytics;
  const wasVisible = useRef(false);
  const completed = useRef(false);

  useEffect(() => {
    if (!visible) setCopied(false);

    if (visible && !wasVisible.current) {
      completed.current = false;
      trackShareClicked(target.current);
    } else if (!visible && wasVisible.current && !completed.current) {
      trackShareCompleted({ ...target.current, outcome: 'dismissed' });
    }
    wasVisible.current = visible;
    // `analytics` is deliberately not a dependency: it is read through a ref so
    // an inline object literal at the call site cannot re-fire the edges.
  }, [visible]);

  const reportShared = (channel: ShareChannel) => {
    completed.current = true;
    trackShareCompleted({ ...target.current, channel, outcome: channel === 'clipboard' ? 'copied' : 'shared' });
  };

  // AMB-224 — Facebook is blocked / restricted in RU; the FB share intent
  // wouldn't resolve anyway, so the tile is dropped for users in restricted
  // regions. Telegram/WhatsApp/Email + Copy Link still work.
  const hideRestrictedShares = isRestrictedRegion(user);

  const effectiveSubject = subject ?? title;
  const effectiveMessage = message ?? `${title} — ${url}`;

  const openExternal = async (kind: ShareKind) => {
    let target = '';
    if (kind === 'telegram') {
      // CORE-235 — on native, Linking.openURL of the https t.me form gets
      // routed to the Telegram app, which doesn't handle /share/url and
      // bounces the user to the app store ("downloads Telegram"). The tg://
      // deep link opens the share composer directly; if Telegram isn't
      // installed canOpenURL is false → native share-sheet fallback below.
      // Web keeps the https form (window.open handles it correctly).
      target = Platform.OS === 'web'
        ? `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(effectiveSubject)}`
        : `tg://msg_url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(effectiveSubject)}`;
    } else if (kind === 'whatsapp') {
      target = `https://wa.me/?text=${encodeURIComponent(effectiveMessage)}`;
    } else if (kind === 'facebook') {
      target = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    } else if (kind === 'email') {
      target = `mailto:?subject=${encodeURIComponent(effectiveSubject)}&body=${encodeURIComponent(effectiveMessage)}`;
    }

    try {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.open(target, '_blank', 'noopener,noreferrer');
      } else {
        const supported = await Linking.canOpenURL(target);
        if (supported) {
          await Linking.openURL(target);
        } else {
          // No installed app intent — fall back to native share sheet so the
          // user can pick whatever they have.
          // No `url` field — the message already ends with the link, and iOS hands
          // the sheet a second item that «Скопировать» concatenates (CORE-743).
          await Share.share({ message: effectiveMessage, title: effectiveSubject });
        }
      }
    } catch {
      /* swallow — user cancelled or no handler */
    }
    // Reported for the tile the user picked, not for whatever the fallback
    // ended up using: the question the report answers is which channel people
    // reach for. Whether the OS then found an app for it is a different one.
    reportShared(kind);
    onClose();
  };

  const copyLink = async () => {
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      } else {
        await Clipboard.setStringAsync(url);
      }
      setCopied(true);
      reportShared('clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch { /* swallow — keep modal open so user can retry */ }
  };

  const tiles: { key: string; color: string; icon: keyof typeof Ionicons.glyphMap; label: string; onPress: () => void }[] = [
    { key: 'telegram', color: '#26A5E4', icon: 'paper-plane',   label: 'Telegram', onPress: () => openExternal('telegram') },
    { key: 'whatsapp', color: '#25D366', icon: 'logo-whatsapp', label: 'WhatsApp', onPress: () => openExternal('whatsapp') },
    // AMB-224: Facebook tile hidden for users in restricted regions.
    ...(hideRestrictedShares ? [] : [{ key: 'facebook', color: '#1877F2', icon: 'logo-facebook' as keyof typeof Ionicons.glyphMap, label: 'Facebook', onPress: () => openExternal('facebook') }]),
    { key: 'email',    color: '#6B7280', icon: 'mail',          label: language === 'ru' ? 'Почта' : 'Email', onPress: () => openExternal('email') },
    // CORE-1795 — the OS sheet, when the popup opened in its place (see `onSystemShare`).
    ...(onSystemShare ? [{ key: 'system', color: '#E98245', icon: 'share-outline' as keyof typeof Ionicons.glyphMap, label: language === 'ru' ? 'Ещё' : 'More', onPress: () => { completed.current = true; onSystemShare(); onClose(); } }] : []),
  ];

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={s.overlay} activeOpacity={1} onPress={onClose}>
        {/* CORE-1733 (возврат QA) — общий диалог «Поделиться» (и в Live: CORE-1735 оставил
            его вне семейства live-overlay) — поверхность v4: на затемнении полупрозрачный
            белый .78 читался серым. */}
        <TouchableOpacity activeOpacity={1} style={[s.card, surfaceFill(isDark ? 'dark' : 'light')]}>
          <View style={s.header}>
            <Typography variant="h4" style={{ fontWeight: '700' } as any}>
              {language === 'ru' ? 'Поделиться' : 'Share'}
            </Typography>
            <TouchableOpacity onPress={onClose}>
              <Ionicons name="close" size={24} color={tc.textPrimary} />
            </TouchableOpacity>
          </View>

          {actions && actions.length > 0 ? (
            <View style={s.actions} testID="share-actions">
              {actions.map(a => (
                <ActionRow key={a.key} action={a} accent={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,20,25,0.04)'} textPrimary={tc.textPrimary} textSecondary={tc.textSecondary} />
              ))}
            </View>
          ) : null}

          <Typography variant="body" style={[s.sub, { color: tc.textSecondary }] as any}>
            {language === 'ru'
              ? 'Отправьте друзьям или скопируйте ссылку.'
              : 'Send to friends or copy the link.'}
          </Typography>

          {preview}

          <View style={s.grid}>
            {tiles.map(t => (
              <TouchableOpacity key={t.key} style={s.tile} onPress={t.onPress} activeOpacity={0.8}>
                <View style={[s.circle, { backgroundColor: t.color }]}>
                  <Ionicons name={t.icon} size={22} color="#fff" />
                </View>
                <Typography variant="caption" style={[s.label, { color: tc.textPrimary }] as any}>
                  {t.label}
                </Typography>
              </TouchableOpacity>
            ))}
          </View>

          <TouchableOpacity
            testID="room-share-copy"
            style={[s.copyRow, { borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(15,20,25,0.08)' }]}
            onPress={copyLink}
            activeOpacity={0.85}
          >
            <Ionicons name={copied ? 'checkmark-circle' : 'link-outline'} size={20} color={copied ? ACCENT_TEXT : tc.textPrimary} />
            <Typography variant="body" numberOfLines={1} style={[s.url, { color: tc.textSecondary }] as any}>
              {url}
            </Typography>
            {/* Pre-fix: idle bg was `tc.textPrimary`, which is white in dark
                theme — combined with the white text below it produced an
                invisible white-on-white pill. Pin idle bg to a dark/orange
                surface and only flip the text color on contrast. */}
            <View style={[s.copyBtn, { backgroundColor: copied ? '#E98245' : (isDark ? 'rgba(255,255,255,0.16)' : '#0F1419') }]}>
              <Typography variant="button" style={{ color: copied ? ON_ACCENT_TEXT : '#fff', fontSize: 12 } as any}>
                {copied
                  ? (language === 'ru' ? 'Скопировано' : 'Copied')
                  : (language === 'ru' ? 'Копировать' : 'Copy')}
              </Typography>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

/**
 * CORE-1795 — one in-app action: glyph, the visible name, the line under it; a spinner
 * while its data loads, dimmed when it cannot run. Keyboard focus draws a ring (the
 * global CSS erases outlines), Enter / Space press it like a button.
 */
const ActionRow: React.FC<{ action: ShareModalAction; accent: string; textPrimary: string; textSecondary: string }> = ({ action, accent, textPrimary, textSecondary }) => {
  const [focused, setFocused] = useState(false);
  const off = !!action.disabled || !!action.pending;
  return (
    <Pressable
      testID={action.testID}
      accessibilityRole="button"
      accessibilityLabel={action.hint ? `${action.label}. ${action.hint}` : action.label}
      accessibilityState={{ disabled: !!action.disabled, busy: !!action.pending }}
      disabled={off}
      onPress={action.onPress}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={({ hovered, pressed }: any) => [
        s.actionRow,
        { backgroundColor: !off && (hovered || pressed) ? accent : 'transparent', opacity: action.disabled ? 0.55 : 1 },
        Platform.OS === 'web' ? ({ cursor: off ? 'default' : 'pointer', outlineStyle: 'none', boxShadow: focused ? `0 0 0 2px ${ACCENT_TEXT}` : 'none' } as any) : null,
      ]}
    >
      <View style={s.actionGlyph}>
        {action.pending ? <ActivityIndicator size="small" color={ACCENT_TEXT} /> : <Ionicons name={action.icon} size={20} color={ACCENT_TEXT} />}
      </View>
      <View style={s.actionText}>
        <Typography variant="body" style={[s.actionLabel, { color: textPrimary }] as any}>{action.label}</Typography>
        {action.hint ? <Typography variant="caption" style={[s.actionHint, { color: textSecondary }] as any}>{action.hint}</Typography> : null}
      </View>
      {!off ? <Ionicons name="chevron-forward" size={18} color={textSecondary} /> : null}
    </Pressable>
  );
};

const s = StyleSheet.create({
  actions: {
    marginBottom: 16,
    gap: 2,
  } as any,
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    minHeight: 56,
    paddingVertical: 8,
    paddingHorizontal: 10,
    marginHorizontal: -10,
    borderRadius: 14,
  } as any,
  actionGlyph: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(233,130,69,0.14)',
  } as any,
  actionText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  } as any,
  actionLabel: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
  } as any,
  actionHint: {
    fontSize: 12,
    lineHeight: 16,
  } as any,
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    ...(Platform.OS === 'web' && { backdropFilter: 'blur(4px)' } as any),
  } as any,
  card: {
    width: '100%',
    maxWidth: 560,
    borderRadius: 24,
    padding: 24,
    ...(Platform.OS === 'web' && { boxShadow: '0 24px 48px rgba(0,0,0,0.2)' } as any),
  } as any,
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  } as any,
  sub: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 24,
  } as any,
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    rowGap: 16,
    columnGap: 24,
    marginBottom: 24,
  } as any,
  tile: {
    alignItems: 'center',
    gap: 8,
    minWidth: 72,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  } as any,
  circle: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
  } as any,
  label: {
    fontSize: 12,
    fontWeight: '600',
    textAlign: 'center',
  } as any,
  copyRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 14,
    borderWidth: 1,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  } as any,
  url: {
    flex: 1,
    fontSize: 13,
  } as any,
  copyBtn: {
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: 999,
  } as any,
});

export default ShareModal;
